import { useCallback, useEffect, useRef, useState } from 'react';
import { Card, DataTable, Drawer, Modal, FormField, TextInput, TextArea, StatusBadge, Toggle, ConfirmDialog } from '@/components/ui';
import type { DataTableColumn } from '@/components/ui/DataTable';
import { Icon } from '@/components/ui/Icon';
import { adminApi, type HubWebhook, type HubDelivery, type HubDeliveryStatus, type HubWebhookFilters, type WebhookHeader, type WebhookMethod, type WebhookContentType } from '@/shared/api/adminApi';
import { toast } from '@/components/ui/Toast';
import { fmtDateTime, JsonDetails, SecretModal } from './common';
import { GENERAL_VARS, varsForEvents } from './eventVars';
import { VarChip } from './VarChip';

interface CampaignOpt { id: number; name: string }
interface DispositionOpt { code: string; label: string; campaign_id: number | null }

const DELIVERY_TONE: Record<HubDeliveryStatus, 'ok' | 'warn' | 'danger' | 'neutral'> = {
  success: 'ok',
  pending: 'warn',
  failed: 'danger',
  dead: 'neutral',
};
const DELIVERY_HE: Record<HubDeliveryStatus, string> = {
  success: 'הצליח',
  pending: 'ממתין',
  failed: 'נכשל',
  dead: 'מת',
};


export function WebhooksTab({ webhooks, catalog, onChanged }: { webhooks: HubWebhook[]; catalog: Record<string, string>; onChanged: () => void }) {
  const [editing, setEditing] = useState<HubWebhook | 'new' | null>(null);
  const [secret, setSecret] = useState<string | null>(null);
  const [deliveriesFor, setDeliveriesFor] = useState<HubWebhook | null>(null);
  const [confirmDel, setConfirmDel] = useState<HubWebhook | null>(null);
  const [rotateFor, setRotateFor] = useState<HubWebhook | null>(null);
  const [testingId, setTestingId] = useState<number | null>(null);
  const [campaigns, setCampaigns] = useState<CampaignOpt[]>([]);
  const [dispositions, setDispositions] = useState<DispositionOpt[]>([]);

  // בורר הטווח (scope) של ה-webhook זקוק לרשימת הקמפיינים והסטטוסים — נטענים פעם אחת.
  useEffect(() => {
    void adminApi.campaigns().then((r) => setCampaigns(((r.data as CampaignOpt[]) ?? []).map((c) => ({ id: c.id, name: c.name })))).catch(() => {});
    void adminApi.dispositions().then((r) => setDispositions((r.data as DispositionOpt[]) ?? [])).catch(() => {});
  }, []);
  const campaignName = (id: number | null | undefined) => (id == null ? null : campaigns.find((c) => c.id === id)?.name ?? `#${id}`);

  async function toggleActive(w: HubWebhook, active: boolean) {
    try {
      await adminApi.updateHubWebhook(w.id, { active });
      onChanged();
    } catch {
      toast('שגיאה בעדכון', 'danger');
    }
  }

  async function test(w: HubWebhook) {
    setTestingId(w.id);
    try {
      const r = await adminApi.testHubWebhook(w.id);
      if (r.ok) toast(`הבדיקה הצליחה — היעד ענה ${r.status ?? ''} תוך ${r.latency_ms ?? '?'}ms`, 'ok');
      else toast(`הבדיקה נכשלה — ${r.status ?? ''} ${r.error ?? ''}`.trim(), 'danger');
    } catch {
      toast('שגיאה בבדיקה', 'danger');
    } finally {
      setTestingId(null);
    }
  }

  async function rotate(w: HubWebhook) {
    try {
      const r = await adminApi.rotateHubWebhookSecret(w.id);
      setRotateFor(null);
      setSecret(r.signing_secret);
    } catch {
      toast('שגיאה בסיבוב הסוד', 'danger');
    }
  }

  async function del(w: HubWebhook) {
    try {
      await adminApi.deleteHubWebhook(w.id);
      toast('נמחק', 'ok');
      setConfirmDel(null);
      onChanged();
    } catch {
      toast('שגיאה במחיקה', 'danger');
    }
  }

  const cols: DataTableColumn<HubWebhook>[] = [
    { key: 'name', he: 'שם', render: (r) => r.name || '—' },
    { key: 'url', he: 'URL', render: (r) => <span dir="ltr" style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--t-xs)' }}>{truncate(r.url, 40)}</span> },
    { key: 'events', he: 'אירועים', align: 'center', render: (r) => r.events?.length ?? 0 },
    {
      key: 'scope', he: 'טווח', render: (r) => {
        const f = r.filters;
        if (!f?.campaign_id) return <StatusBadge tone="neutral" size="sm">גלובלי</StatusBadge>;
        return (
          <span style={{ display: 'inline-flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
            <StatusBadge tone="info" size="sm">קמפיין: {campaignName(f.campaign_id)}</StatusBadge>
            {f.dispositions && f.dispositions.length > 0 && (
              <span style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>{f.dispositions.length} סטטוסים</span>
            )}
          </span>
        );
      },
    },
    {
      key: 'health', he: 'בריאות 24ש׳', align: 'center', render: (r) => {
        const d = r.deliveries_24h ?? 0;
        const s = r.success_24h ?? 0;
        if (d === 0) return <span style={{ color: 'var(--muted)' }}>—</span>;
        const rate = s / d;
        const tone = rate >= 0.95 ? 'var(--ok)' : rate >= 0.7 ? 'var(--warn)' : 'var(--danger)';
        return <span style={{ color: tone }}>{s}/{d}</span>;
      },
    },
    { key: 'active', he: 'פעיל', align: 'center', render: (r) => <div onClick={(e) => e.stopPropagation()}><Toggle checked={r.active} onChange={(v) => toggleActive(r, v)} /></div> },
    {
      key: '_actions', he: 'פעולות', render: (r) => (
        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }} onClick={(e) => e.stopPropagation()}>
          {/* בדיקה חיה: שולח אירוע דוגמה חתום ליעד ומציג סטטוס+זמן תגובה. */}
          <button
            type="button"
            className="btn"
            onClick={() => void test(r)}
            disabled={testingId === r.id}
            title="שולח אירוע בדיקה חתום ל-URL ומציג את תשובת היעד"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 'var(--t-xs)', padding: '3px 10px' }}
          >
            <Icon name="bolt" size={13} />
            {testingId === r.id ? 'בודק…' : 'בדיקה'}
          </button>
          <button type="button" className="btn ghost" onClick={() => setEditing(r)} aria-label="עריכה" title="עריכה"><Icon name="edit" size={14} /></button>
          <button type="button" className="btn ghost" onClick={() => setDeliveriesFor(r)} aria-label="היסטוריית משלוחים" title="היסטוריית משלוחים"><Icon name="history" size={14} /></button>
          <button type="button" className="btn ghost" onClick={() => setRotateFor(r)} aria-label="החלפת סוד חתימה" title="החלפת סוד חתימה"><Icon name="lock" size={14} /></button>
          <button type="button" className="btn ghost" onClick={() => setConfirmDel(r)} aria-label="מחיקה" title="מחיקה"><Icon name="trash" size={14} /></button>
        </div>
      ),
    },
  ];

  return (
    <div className="stack">
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button type="button" className="btn primary" onClick={() => setEditing('new')}>+ Webhook חדש</button>
      </div>
      <Card padded={false}>
        <DataTable<HubWebhook> columns={cols} rows={webhooks} emptyMessage="אין Webhooks. צור אחד כדי לקבל התראות על אירועים." />
      </Card>

      {editing && (
        <WebhookForm
          webhook={editing === 'new' ? null : editing}
          catalog={catalog}
          campaigns={campaigns}
          dispositions={dispositions}
          onClose={() => setEditing(null)}
          onSaved={(newSecret) => { setEditing(null); if (newSecret) setSecret(newSecret); onChanged(); }}
        />
      )}

      {secret && (
        <SecretModal title="סוד החתימה" label="Signing Secret" secret={secret} onClose={() => setSecret(null)} />
      )}

      {deliveriesFor && (
        <DeliveriesDrawer webhook={deliveriesFor} onClose={() => setDeliveriesFor(null)} />
      )}

      <ConfirmDialog
        isOpen={!!rotateFor}
        onClose={() => setRotateFor(null)}
        onConfirm={() => { if (rotateFor) void rotate(rotateFor); }}
        title="סיבוב סוד חתימה"
        message="הסוד הקיים יפסיק לעבוד מיד. להמשיך?"
        confirmLabel="סובב"
        tone="warn"
      />

      <ConfirmDialog
        isOpen={!!confirmDel}
        onClose={() => setConfirmDel(null)}
        onConfirm={() => { if (confirmDel) void del(confirmDel); }}
        title="מחיקת Webhook"
        message={`למחוק את "${confirmDel?.name}"?`}
        confirmLabel="מחק"
        tone="danger"
      />
    </div>
  );
}

function WebhookForm({ webhook, catalog, campaigns, dispositions, onClose, onSaved }: {
  webhook: HubWebhook | null;
  catalog: Record<string, string>;
  campaigns: CampaignOpt[];
  dispositions: DispositionOpt[];
  onClose: () => void;
  onSaved: (secret?: string) => void;
}) {
  const [name, setName] = useState(webhook?.name ?? '');
  const [description, setDescription] = useState(webhook?.description ?? '');
  const [url, setUrl] = useState(webhook?.url ?? '');
  const [events, setEvents] = useState<string[]>(webhook?.events ?? []);
  // טווח: גלובלי (campaign_id=null) או פר-קמפיין (+סטטוסים אופציונליים).
  const [scope, setScope] = useState<'global' | 'campaign'>(webhook?.filters?.campaign_id ? 'campaign' : 'global');
  const [campaignId, setCampaignId] = useState<string>(webhook?.filters?.campaign_id ? String(webhook.filters.campaign_id) : '');
  const [statuses, setStatuses] = useState<string[]>(webhook?.filters?.dispositions ?? []);
  const [method, setMethod] = useState<WebhookMethod>(webhook?.http_method ?? 'POST');
  const [headers, setHeaders] = useState<WebhookHeader[]>(webhook?.headers ?? []);
  const [contentType, setContentType] = useState<WebhookContentType>(webhook?.content_type ?? 'json');
  const [bodyTemplate, setBodyTemplate] = useState<string>(webhook?.body_template ?? '');
  const [batchWindow, setBatchWindow] = useState<number>(webhook?.batch_window_sec ?? 0);
  const [batchMax, setBatchMax] = useState<number>(webhook?.batch_max ?? 50);
  // שליחה מרוכזת בונה מערך אירועים — לא מסתדר עם תבנית פר-אירוע או form.
  const batchBlocked = contentType === 'form' || bodyTemplate.trim() !== '';
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  // עוקבים אחרי השדה האחרון שהיה בפוקוס (גוף/כתובת/ערך-header), כדי
  // שלחיצה על משתנה תכניס אותו לשדה הפעיל במיקום הסמן. ברירת מחדל: הגוף.
  type ActiveField = { kind: 'body' } | { kind: 'url' } | { kind: 'header'; index: number };
  const activeField = useRef<ActiveField>({ kind: 'body' });
  const fieldEls = useRef<Record<string, HTMLInputElement | HTMLTextAreaElement | null>>({});
  const fieldKey = (f: ActiveField) => (f.kind === 'header' ? `header-${f.index}` : f.kind);

  function insertVar(token: string) {
    const f = activeField.current;
    const el = fieldEls.current[fieldKey(f)];
    const current = f.kind === 'body' ? bodyTemplate : f.kind === 'url' ? url : (headers[f.index]?.value ?? '');
    const start = el?.selectionStart ?? current.length;
    const end = el?.selectionEnd ?? current.length;
    const next = current.slice(0, start) + token + current.slice(end);

    if (f.kind === 'body') setBodyTemplate(next);
    else if (f.kind === 'url') setUrl(next);
    else setHeader(f.index, { value: next });

    requestAnimationFrame(() => {
      const pos = start + token.length;
      el?.focus();
      el?.setSelectionRange?.(pos, pos);
    });
  }

  function setHeader(i: number, patch: Partial<WebhookHeader>) {
    setHeaders((cur) => cur.map((h, idx) => (idx === i ? { ...h, ...patch } : h)));
  }
  const cleanHeaders = () => headers.filter((h) => h.key.trim());

  async function testNow() {
    if (!url.trim()) { toast('הזן כתובת', 'danger'); return; }
    setTesting(true);
    try {
      const r = await adminApi.testDraftHubWebhook({ url: url.trim(), headers: cleanHeaders(), http_method: method, body_template: bodyTemplate.trim() || null, content_type: contentType });
      if (r.ok) toast(`הצליח · ${r.status ?? ''} · ${r.latency_ms ?? '?'}ms`, 'ok');
      else toast(`נכשל · ${r.status ?? ''} ${r.error ?? ''}`.trim(), 'danger');
    } catch (e) {
      toast((e as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'שגיאה בבדיקה', 'danger');
    } finally {
      setTesting(false);
    }
  }

  function toggleEvent(name: string) {
    setEvents((cur) => (cur.includes(name) ? cur.filter((e) => e !== name) : [...cur, name]));
  }
  function toggleStatus(code: string) {
    setStatuses((cur) => (cur.includes(code) ? cur.filter((c) => c !== code) : [...cur, code]));
  }

  // סטטוסים זמינים לקמפיין הנבחר: גלובליים (campaign_id=null) + של אותו קמפיין.
  const cid = campaignId ? Number(campaignId) : null;
  const scopedDispositions = dispositions.filter((d) => d.campaign_id == null || d.campaign_id === cid);

  function buildFilters(): HubWebhookFilters | null {
    if (scope === 'global' || !cid) return null;
    return { campaign_id: cid, dispositions: statuses.length ? statuses : null };
  }

  async function save() {
    if (!name.trim() || !url.trim()) { toast('שם וכתובת נדרשים', 'danger'); return; }
    if (events.length === 0) { toast('בחר לפחות אירוע אחד', 'danger'); return; }
    if (scope === 'campaign' && !cid) { toast('בחר קמפיין', 'danger'); return; }
    setSaving(true);
    try {
      const filters = buildFilters();
      const hdrs = cleanHeaders();
      const bt = bodyTemplate.trim() || null;
      const batch = { batch_window_sec: batchBlocked ? 0 : batchWindow, batch_max: batchMax };
      if (webhook) {
        await adminApi.updateHubWebhook(webhook.id, { name: name.trim(), url: url.trim(), events, filters, headers: hdrs, http_method: method, body_template: bt, content_type: contentType, ...batch });
        toast('נשמר', 'ok');
        onSaved();
      } else {
        const r = await adminApi.createHubWebhook({ name: name.trim(), description: description || undefined, url: url.trim(), events, filters, headers: hdrs, http_method: method, body_template: bt, content_type: contentType, ...batch });
        toast('נוצר', 'ok');
        onSaved(r.signing_secret);
      }
    } catch (e: unknown) {
      toast((e as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'שגיאה בשמירה', 'danger');
    } finally {
      setSaving(false);
    }
  }

  const entries = Object.entries(catalog);

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={webhook ? 'עריכת Webhook' : 'Webhook חדש'}
      footer={<>
        <button type="button" className="btn" onClick={onClose} disabled={saving}>ביטול</button>
        <button type="button" className="btn" onClick={() => void testNow()} disabled={testing || saving}>
          <Icon name="bolt" size={14} /> {testing ? 'בודק…' : 'בדוק עכשיו'}
        </button>
        <button type="button" className="btn primary" onClick={save} disabled={saving}>{saving ? 'שומר…' : 'שמור'}</button>
      </>}
    >
      <div className="stack">
        <FormField label="שם" required><TextInput value={name} onChange={(e) => setName(e.target.value)} /></FormField>
        {!webhook && <FormField label="תיאור"><TextArea value={description ?? ''} onChange={(e) => setDescription(e.target.value)} rows={2} /></FormField>}
        <FormField label="שיטת HTTP">
          <select value={method} onChange={(e) => setMethod(e.target.value as WebhookMethod)}
            style={{ width: 140, padding: 10, border: '1px solid var(--line)', borderRadius: 'var(--r-2)', background: 'var(--surface)' }}>
            {(['POST', 'PUT', 'PATCH', 'GET'] as WebhookMethod[]).map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        </FormField>
        <FormField label="כתובת (URL)" required
          hint="אפשר פרמטרים דינמיים בכתובת: {{event}}, {{tenant_id}}, {{data.lead_id}} וכו׳ — מוחלפים מתוך נתוני האירוע.">
          <TextInput ref={(el) => { fieldEls.current['url'] = el; }} onFocus={() => { activeField.current = { kind: 'url' }; }}
            value={url} onChange={(e) => setUrl(e.target.value)} dir="ltr" placeholder="https://example.com/hook?event={{event}}" />
        </FormField>

        {/* Headers מותאמים — טוקנים / Authorization / API-key */}
        <FormField label="Headers מותאמים" hint="נשלחים בנוסף לחתימת ה-HMAC (X-Merkaziya-*). למשל Authorization: Bearer <token>.">
          <div className="stack" style={{ gap: 6 }}>
            {headers.map((h, i) => (
              <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <TextInput value={h.key} onChange={(e) => setHeader(i, { key: e.target.value })} placeholder="Header" dir="ltr" style={{ flex: '0 0 40%' }} />
                <TextInput ref={(el) => { fieldEls.current[`header-${i}`] = el; }} onFocus={() => { activeField.current = { kind: 'header', index: i }; }}
                  value={h.value} onChange={(e) => setHeader(i, { value: e.target.value })} placeholder="ערך" dir="ltr" style={{ flex: 1 }} />
                <button type="button" className="btn ghost" onClick={() => setHeaders((cur) => cur.filter((_, idx) => idx !== i))} aria-label="הסר header">
                  <Icon name="trash" size={14} />
                </button>
              </div>
            ))}
            <div>
              <button type="button" className="btn" onClick={() => setHeaders((cur) => [...cur, { key: '', value: '' }])}>+ הוסף header</button>
            </div>
          </div>
        </FormField>

        {/* שליחה מרוכזת (bulk) — אירועים נערמים ונשלחים כמערך אחד כל חלון */}
        <FormField label="שליחה מרוכזת (Bulk)"
          hint={batchBlocked
            ? 'לא זמין יחד עם גוף מותאם או form — הריכוז שולח מערך אירועים בגוף JSON אחד.'
            : 'במקום בקשה לכל אירוע — האירועים נשלחים כמערך אחד ({batch:true, events:[…]}) כל חלון זמן. מונע חסימות קצב (429) אצל היעד.'}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <select
              value={batchBlocked ? 0 : batchWindow}
              disabled={batchBlocked}
              onChange={(e) => setBatchWindow(Number(e.target.value))}
              style={{ width: 220, padding: 10, border: '1px solid var(--line)', borderRadius: 'var(--r-2)', background: 'var(--surface)', opacity: batchBlocked ? 0.6 : 1 }}
            >
              <option value={0}>כבוי — בקשה לכל אירוע</option>
              <option value={15}>מרוכז — כל 15 שניות</option>
              <option value={30}>מרוכז — כל 30 שניות</option>
              <option value={60}>מרוכז — כל דקה</option>
            </select>
            {batchWindow > 0 && !batchBlocked && (
              <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 'var(--t-sm)', color: 'var(--muted)' }}>
                מקס׳ אירועים למשלוח
                <input
                  type="number" min={1} max={500} value={batchMax}
                  onChange={(e) => setBatchMax(Math.max(1, Math.min(500, Number(e.target.value) || 1)))}
                  style={{ width: 90, padding: 8, border: '1px solid var(--line)', borderRadius: 'var(--r-2)', background: 'var(--surface)' }}
                />
              </label>
            )}
          </div>
        </FormField>

        {/* גוף מותאם + Content-Type */}
        <FormField label="Content-Type">
          <select value={contentType} onChange={(e) => setContentType(e.target.value as WebhookContentType)}
            style={{ width: 220, padding: 10, border: '1px solid var(--line)', borderRadius: 'var(--r-2)', background: 'var(--surface)' }}>
            <option value="json">application/json</option>
            <option value="form">x-www-form-urlencoded</option>
          </select>
        </FormField>
        <FormField label="גוף מותאם (Body)"
          hint="ריק = ברירת המחדל: {event, tenant_id, timestamp, data}. אפשר JSON משלך. ב-form-encoded — אובייקט JSON שממנו נבנים שדות הטופס.">
          <TextArea ref={(el) => { fieldEls.current['body'] = el; }} onFocus={() => { activeField.current = { kind: 'body' }; }}
            value={bodyTemplate} onChange={(e) => setBodyTemplate(e.target.value)} dir="ltr" rows={5}
            placeholder={'{\n  "text": "ליד {{data.name}} ({{data.phone}}) — {{event}}"\n}'} />
          {/* משתנים לחיצים — מסוננים לפי האירועים שנבחרו. לחיצה מכניסה במיקום הסמן. */}
          <div style={{ marginTop: 6 }}>
            <div style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)', marginBottom: 4 }}>
              {events.length === 0
                ? 'לחץ בשדה (כתובת / ערך header / גוף) ואז על משתנה כדי להכניס אותו שם. בחר אירועים כדי לצמצם לרלוונטיים:'
                : 'לחץ בשדה (כתובת / ערך header / גוף) ואז על משתנה של האירועים שנבחרו כדי להכניס:'}
            </div>
            <div className="stack" style={{ gap: 6 }}>
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
                <span style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)', minWidth: 64 }}>כללי:</span>
                {GENERAL_VARS.map((v) => (
                  <VarChip key={v.token} v={v} onInsert={insertVar} />
                ))}
              </div>
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
                <span style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)', minWidth: 64 }}>נתוני האירוע:</span>
                {varsForEvents(events).map((v) => (
                  <VarChip key={v.token} v={v} onInsert={insertVar} />
                ))}
              </div>
            </div>
          </div>
        </FormField>

        <FormField label="אירועים">
          {entries.length === 0 ? (
            <span style={{ color: 'var(--muted)', fontSize: 'var(--t-sm)' }}>אין אירועים בקטלוג.</span>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 4 }}>
              {entries.map(([name, label]) => (
                <label key={name} style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 'var(--t-sm)', cursor: 'pointer' }}>
                  <input type="checkbox" checked={events.includes(name)} onChange={() => toggleEvent(name)} />
                  {label}
                </label>
              ))}
            </div>
          )}
        </FormField>

        {/* טווח (scope) — גלובלי / פר-קמפיין (+סטטוסים) */}
        <FormField label="טווח" hint="הגבל את ה-webhook לקמפיין ולסטטוסים מסוימים, או השאר גלובלי לכל האירועים.">
          <div style={{ display: 'grid', gap: 4 }}>
            <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 'var(--t-sm)', cursor: 'pointer' }}>
              <input type="radio" name="wh-scope" checked={scope === 'global'} onChange={() => setScope('global')} />
              גלובלי — כל הקמפיינים
            </label>
            <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 'var(--t-sm)', cursor: 'pointer' }}>
              <input type="radio" name="wh-scope" checked={scope === 'campaign'} onChange={() => setScope('campaign')} />
              פר-קמפיין
            </label>
          </div>
        </FormField>

        {scope === 'campaign' && (
          <>
            <FormField label="קמפיין" required>
              <select
                value={campaignId}
                onChange={(e) => { setCampaignId(e.target.value); setStatuses([]); }}
                style={{ width: '100%', padding: 10, border: '1px solid var(--line)', borderRadius: 'var(--r-2)', background: 'var(--surface)' }}
              >
                <option value="">— בחר קמפיין —</option>
                {campaigns.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </FormField>
            {cid && (
              <FormField label="סטטוסים (אופציונלי)" hint="ריק = כל הסטטוסים. בחר כדי לקבל רק שינויים לסטטוסים אלה.">
                {scopedDispositions.length === 0 ? (
                  <span style={{ color: 'var(--muted)', fontSize: 'var(--t-sm)' }}>אין קודי סיום מוגדרים לקמפיין זה.</span>
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 4 }}>
                    {scopedDispositions.map((d) => (
                      <label key={d.code} style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 'var(--t-sm)', cursor: 'pointer' }}>
                        <input type="checkbox" checked={statuses.includes(d.code)} onChange={() => toggleStatus(d.code)} />
                        {d.label}
                      </label>
                    ))}
                  </div>
                )}
              </FormField>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}

function DeliveriesDrawer({ webhook, onClose }: { webhook: HubWebhook; onClose: () => void }) {
  const [rows, setRows] = useState<HubDelivery[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError(false);
    adminApi.hubWebhookDeliveries(webhook.id)
      .then(setRows)
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [webhook.id]);

  useEffect(() => { load(); }, [load]);

  async function redeliver(id: number) {
    try {
      await adminApi.redeliverHubDelivery(id);
      toast('נשלח מחדש', 'ok');
      load();
    } catch {
      toast('שגיאה בשליחה מחדש', 'danger');
    }
  }

  return (
    <Drawer isOpen onClose={onClose} title={`משלוחים · ${webhook.name}`} width={560}>
      {loading ? (
        <div style={{ color: 'var(--muted)' }}>טוען…</div>
      ) : error ? (
        <div className="stack"><div style={{ color: 'var(--danger)' }}>שגיאה בטעינה.</div><button type="button" className="btn" onClick={load}>נסה שוב</button></div>
      ) : rows.length === 0 ? (
        <div style={{ color: 'var(--muted)' }}>אין משלוחים.</div>
      ) : (
        <div className="stack">
          {rows.map((d) => (
            <Card key={d.id}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <StatusBadge tone={DELIVERY_TONE[d.status]} size="sm">{DELIVERY_HE[d.status]}</StatusBadge>
                  <span style={{ fontSize: 'var(--t-sm)' }}>{d.event}</span>
                  {d.http_status != null && <span style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>HTTP {d.http_status}</span>}
                  {d.latency_ms != null && <span style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>{d.latency_ms}ms</span>}
                  <span style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>ניסיונות: {d.attempts}</span>
                </div>
                <button type="button" className="btn ghost" onClick={() => redeliver(d.id)}>שלח שוב</button>
              </div>
              <div style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)', marginTop: 4 }}>{fmtDateTime(d.created_at)}</div>
              <div style={{ marginTop: 6 }}>
                <JsonDetails label="Payload" value={d.payload} />
                {d.response_snippet && <JsonDetails label="תגובה" value={d.response_snippet} />}
              </div>
            </Card>
          ))}
        </div>
      )}
    </Drawer>
  );
}

function truncate(s: string, n: number): string {
  return s.length > n ? s.slice(0, n) + '…' : s;
}
